import {
	Component,
	OnChanges,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTabGroup, MatTab, MatTabContent } from '@angular/material/tabs';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { ApiService, QueryParam } from 'src/app/services/api.services';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { UserProfileService } from 'src/app/services/user-profile.service';
import Property = Components.Schemas.Location;
import { AUDIT_LOG_COLUMN_PROPERTY } from 'src/app/app.constants';
import { AppTabNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import { TabAutomationIdDirective } from '../../../directives/tab-automation-id.directive';
import { QueryStringTabGroupDirective } from '../../../directives/query-string-tab-group.directive';
import { PropertyAboutComponent } from './property-about/property-about.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { AllDevicesComponent } from '../../device/main/all-devices/all-devices.component';
import { ClientListTableComponent } from '../../client/client-list-table/client-list-table.component';
import { PropertyEventHistoryComponent } from './property-event-history/property-event-history.component';
import { AuditLogListComponent } from '../../audit-log-list/audit-log-list.component';

@Component({
	selector: 'app-property-details',
	templateUrl: './property-details.component.html',
	styleUrls: [
		'./property-details.component.css',
		'../../common/header-box.scss',
		'../../common/main.scss',
	],
	imports: [
		AppTabNavigationHeaderComponent,
		MatTabGroup,
		TabAutomationIdDirective,
		QueryStringTabGroupDirective,
		MatTab,
		MatTabContent,
		PropertyAboutComponent,
		BreadcrumbLabelDirective,
		AllDevicesComponent,
		ClientListTableComponent,
		PropertyEventHistoryComponent,
		AuditLogListComponent,
	],
})
export class PropertyDetailsComponent implements OnInit, OnChanges {
	static defaultPageSize = 30;

	viewingCompanyId?: string;
	propertyId = this.route.snapshot.params.id;
	property!: Property;
	canAccessEventHistory = false;
	canAccessAuditLogs: boolean = false;
	canViewClients: boolean = false;
	hiddenAuditLogColumns = [AUDIT_LOG_COLUMN_PROPERTY];

	aboutBreadcrumbLabel = '';
	devicesBreadcrumbLabel = '';
	eventHistoryBreadcrumbLabel = '';
	auditLogBreadcrumbLabel = '';

	@ViewChild(MatTabGroup) tabGroup?: MatTabGroup;

	initialTabIndex: number = 0;

	constructor(
		private service: ApiService,
		private route: ActivatedRoute,
		private router: Router,
		private readonly systemCapabilityService: SystemCapabilityService,
		private readonly userProfileService: UserProfileService
	) {}

	updateBreadcrumbLabels(): void {
		this.aboutBreadcrumbLabel = `${this.property.name}`;
		this.devicesBreadcrumbLabel = `${this.property.name}`;
		this.eventHistoryBreadcrumbLabel = `${this.property.name}`;
		this.auditLogBreadcrumbLabel = `${this.property.name}`;
	}

	async ngOnInit(): Promise<void> {
		await this.populateProperty();
		this.updateBreadcrumbLabels();

		// Check for openTab parameter, if navigating to clients tab, show it immediately before any rendering to
		// to prevent flickering. If the user doesn't have access to any property clients, no data will be shown
		// because of api security
		const snapshot = this.route.snapshot.queryParams;
		if (snapshot.openTab === 'clients') {
			this.canViewClients = true;
			this.initialTabIndex = 2; // Clients tab index
		}

		await this.updateEventHistoryAccess();
		await this.checkPropertyHasClients();

		// Company-shell nest (ITE-11994) uses :companyId; legacy /property-details uses ?companyId=.
		const routeCompanyId = this.route.snapshot.params.companyId;
		if (routeCompanyId) {
			this.viewingCompanyId = routeCompanyId;
		}

		this.route.queryParams.subscribe((params) => {
			if (params.companyId) {
				this.viewingCompanyId = params.companyId;
			}
		});
	}

	async ngOnChanges(changes: SimpleChanges): Promise<void> {
		await this.populateProperty();
		this.updateBreadcrumbLabels();
	}

	async populateProperty(): Promise<void> {
		this.property = await this.service.getProperty(this.propertyId);
	}

	get propertyName(): string {
		return this.property?.name ?? '';
	}

	readonly filteredDevicesByPropertyId = (filter: QueryParam[]) => {
		if (this.viewingCompanyId) {
			filter.push({ param: 'companyId', value: this.viewingCompanyId });
		}
		return this.service.getDevicesByPropertyId(this.propertyId, filter);
	};

	async updateEventHistoryAccess(): Promise<void> {
		// Only SentriKey Access (Sentrilock) users can access event history
		const userProfile = await this.userProfileService.getUserProfile();
		const isSentriKeyAccessUser =
			userProfile.activeRoleDetails?.company?.isSentrilock ?? false;

		this.canAccessEventHistory =
			isSentriKeyAccessUser &&
			((this.systemCapabilityService.has(
				SystemCapabilityEnum.propertyManagement
			) &&
				this.systemCapabilityService.has(
					SystemCapabilityEnum.globalCompanyManagement
				)) ||
				this.systemCapabilityService.has(SystemCapabilityEnum.runReports));

		// Backend determines if audit logs can be viewed based on:
		// 1. Requesting user has audit capability, AND
		// 2. Either requesting user is Sentrilock OR property belongs to requesting user's company
		this.canAccessAuditLogs = this.property?.canViewAuditLogs ?? false;
	}

	async checkPropertyHasClients(): Promise<void> {
		try {
			// Check if user is in a care company
			const userProfile = await this.userProfileService.getUserProfile();
			const activeCompanyId = userProfile?.activeRoleDetails?.company?.id;
			let isCareCompany = false;

			if (activeCompanyId) {
				const companyData = await this.service.getCompanyById(activeCompanyId);
				isCareCompany = companyData?.company?.companyType === 'care';
			}

			// Check if there are clients
			const clients = await this.service.getClientsByLocationId(
				this.propertyId,
				[
					{ param: 'rowCount', value: '1' },
					{ param: 'rowIndex', value: '0' },
				]
			);
			const hasClients = clients && clients.length > 0;

			// Show tab if user is in a care company OR there are clients
			this.canViewClients = isCareCompany || hasClients;
		} catch (error) {
			this.canViewClients = false;
		}
	}
}
